import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Typography,
} from '@mui/joy';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { BackendContainer } from '@/components/backend/BackendContainer';
import { NoAdminlevelAssignedAlert } from '@/components/content/NoAdminlevelAssignedAlert';
import { CheckboxGroup } from '@/components/form-elements/CheckboxGroup';
import { I18N_DE } from '@/i18n/de';
import { makeMetaTitle } from '@/modules/META';
import { UserGuard } from '@/modules/UserGuard';
import type { UpdateUserDto } from '@/modules/api';
import { AdminLevelUserStatus, ReportMainCategory, UserNotificationInterval } from '@/modules/api';
import { backendApiUsersUpdateMutation } from '@/modules/api/@tanstack/react-query.gen';
import { useUserSuspenseQuery } from '@/queries/useUserSuspenseQuery';
import { useReportsTableStore } from '@/stores/useReportsTableStore';

export const Route = createFileRoute('/verwaltung/profil')({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
  head: () => ({
    meta: [makeMetaTitle(['Profil'])],
  }),
});

type FormData = Required<
  Pick<UpdateUserDto, 'firstName' | 'lastName' | 'notificationInterval' | 'notificationReportMainCategories'>
>;

function RouteComponent() {
  const { setColumnFilters } = useReportsTableStore((state) => state.actions);

  const { data: loginUser } = useUserSuspenseQuery();
  const user = UserGuard.isUser(loginUser) ? loginUser : null;

  const { mutateAsync } = useMutation(backendApiUsersUpdateMutation());

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
    watch,
  } = useForm<FormData>({
    defaultValues: {
      firstName: user?.firstName,
      lastName: user?.lastName,
      notificationInterval: user?.notificationInterval,
      notificationReportMainCategories: user?.notificationReportMainCategories ?? [],
    },
  });

  useEffect(() => {
    if (user) {
      reset({
        firstName: user?.firstName,
        lastName: user?.lastName,
        notificationInterval: user?.notificationInterval,
        notificationReportMainCategories: user?.notificationReportMainCategories,
      });
    }
  }, [user, reset]);

  return (
    user && (
      <BackendContainer>
        {!user.adminLevels?.find(({ status }) => status === AdminLevelUserStatus.ACTIVE) && (
          <NoAdminlevelAssignedAlert />
        )}

        <form
          onSubmit={handleSubmit(async (data) => {
            await mutateAsync({
              path: {
                userId: user.id,
              },
              body: data,
            });

            setColumnFilters((old) => [...old.filter(({ id }) => id !== 'mainCategory')]);
          })}
        >
          <Stack gap={2}>
            <Typography level="h3" marginBottom={2}>
              Profil
            </Typography>

            <FormControl error={!!errors.firstName}>
              <FormLabel>Vorname</FormLabel>
              <Input {...register('firstName', { required: true })} />
            </FormControl>

            <FormControl error={!!errors.lastName}>
              <FormLabel>Nachname</FormLabel>
              <Input {...register('lastName', { required: true })} />
            </FormControl>

            <FormControl>
              <FormLabel>E-Mail</FormLabel>
              <Input readOnly type="email" value={user.email} />
            </FormControl>

            <FormControl>
              <FormLabel>Benachrichtigungsintervall</FormLabel>
              <Controller
                control={control}
                defaultValue={user.notificationInterval}
                name="notificationInterval"
                render={({ field }) => (
                  <Select {...field} onChange={(_, value) => field.onChange(value)}>
                    {Object.values(UserNotificationInterval).map((userNotificationInterval) => (
                      <Option key={userNotificationInterval} value={userNotificationInterval}>
                        {I18N_DE.UserNotificationInterval[userNotificationInterval]}
                      </Option>
                    ))}
                  </Select>
                )}
              />
            </FormControl>

            {watch('notificationInterval') !== UserNotificationInterval.NEVER && (
              <Controller
                control={control}
                name="notificationReportMainCategories"
                render={({ field }) => (
                  <CheckboxGroup
                    i18n={I18N_DE.ReportMainCategory}
                    label="Benachrichtigungskategorien"
                    options={Object.values(ReportMainCategory)}
                    orientation="horizontal"
                    onChange={(newValue) => {
                      if (field.value.includes(newValue)) {
                        field.onChange(field.value.filter((item) => item !== newValue));
                      } else {
                        field.onChange([...field.value, newValue]);
                      }
                    }}
                    values={Object.values(ReportMainCategory).reduce(
                      (previousValue, category) => ({
                        ...previousValue,
                        [category]: field.value.includes(category),
                      }),
                      {} as Record<ReportMainCategory, boolean>,
                    )}
                  >
                    <FormHelperText>
                      Sie erhalten Benachrichtigungen für neue Meldungen, in den von Ihnen gewählten Kategorien,
                      innerhalb der Ihnen zugeordneten Verwaltungsebenen. Die Benachrichtigung zu neuen Meldungen
                      erfolgt jeweils um 8 Uhr.
                    </FormHelperText>

                    <Alert color="primary" sx={{ mt: 1 }} variant="outlined">
                      <Box>
                        <Typography>
                          Die ausgewählten Kategorien gelten gleichzeitig auch im Standardfilter <b>Offene Meldungen</b>{' '}
                          der Meldungentabelle. Nutzen Sie den Standardfilter <b>Alle</b> oder den{' '}
                          <b>Kategoriefilter</b>, um Meldungen weiterer Kategorien anzuzeigen.
                        </Typography>
                      </Box>
                    </Alert>
                  </CheckboxGroup>
                )}
              />
            )}

            <Stack alignItems="flex-end" marginTop={4}>
              <Button disabled={!isDirty} type="submit">
                Speichern
              </Button>
            </Stack>
          </Stack>
        </form>
      </BackendContainer>
    )
  );
}
